import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { features } from '@ui/components/Features';
import { TokenIcon } from '@ui/components/TokenIcon';
import type { TokenProtocol } from '@ui/components/useYourTokenDrawer';
import { isFeatureEnabled } from '@ui/hooks/useIsFeatureEnabled';
import { useTranslations } from '@ui/i18n';
import { getVvsApy } from '@ui/queries/partnerApys';
import { getVvsTvl } from '@ui/queries/vvsTvl';
import { formatNumber, formatPercentage } from '@ui/utils/format';

export const useVnoProtocols = (): TokenProtocol[] => {
  const t = useTranslations();

  const vvsApyRet = useQuery({
    ...getVvsApy(),
  });
  const vvsTvlRet = useQuery({
    ...getVvsTvl(),
  });
  const singleFinanceLendTvlRet = useSingleFinanceLendInfo();
  const singleFinanceFarmTvlRet = useSingleFinanceFarmInfo();

  const result: TokenProtocol[] = [];

  const list: (TokenProtocol | null)[] = [
    isFeatureEnabled(features.tectonicSupportVno)
      ? {
          logo: '/images/use-tectonic.png',
          title: 'Tectonic',
          subtitle: t('Supply {coin}', {
            coin: 'VNO',
          }),
          description: t(
            'Supply and borrow {coin} on the largest money market on Cronos',
            {
              coin: 'VNO',
            },
          ),

          tokens: (
            <>
              <TokenIcon token="vno" w="32px" h="32px" />
              <TokenIcon token="lcro" w="32px" h="32px" />
              <TokenIcon token="latom" w="32px" h="32px" />
            </>
          ),
          url: 'https://app.tectonic.finance/markets/defi',
        }
      : null,
    {
      logo: '/images/single-finance-logo.png',
      title: 'Single Finance',
      titleTail: singleFinanceLendTvlRet.data
        ? formatPercentage(singleFinanceLendTvlRet.data.apy)
        : undefined,
      tvl: singleFinanceLendTvlRet.data
        ? `$${formatNumber(singleFinanceLendTvlRet.data.tvlInUSD, 2)}`
        : undefined,
      subtitle: t('Lend {coin}', {
        coin: 'VNO',
      }),
      description: t('Supply VNO to leveraged yield farmers and earn interest'),
      tokens: <TokenIcon token="vno" w="32px" h="32px" />,
      url: 'https://app.singlefinance.io/lend',
    },
    {
      logo: '/images/single-finance-logo.png',
      title: 'Single Finance',
      titleTail: singleFinanceFarmTvlRet.data
        ? formatPercentage(singleFinanceFarmTvlRet.data.apy)
        : undefined,
      tvl: singleFinanceFarmTvlRet.data
        ? `$${formatNumber(singleFinanceFarmTvlRet.data.tvlInUSD, 2)}`
        : undefined,
      subtitle: t('Leveraged VNO Farm'),
      description: t('Farm VNO with leverage to increase your APY'),
      tokens: <TokenIcon token="vno" w="32px" h="32px" />,
      url: 'https://app.singlefinance.io/farm',
    },
    {
      logo: '/images/use-vvs.png',
      title: 'VVS Finance',
      titleTail: vvsApyRet.data
        ? `${vvsApyRet.data.toFixed(2)}% APY`
        : undefined,
      tvl: vvsTvlRet.data ? `$${formatNumber(vvsTvlRet.data, 2)}` : undefined,
      subtitle: t('Farm {coin}', {
        coin: 'VNO',
      }),
      description: t('Farm VNO to earn VVS rewards'),
      tokens: (
        <>
          <TokenIcon token="vno" w="32px" h="32px" />
          <TokenIcon token="lcro" w="32px" h="32px" />
        </>
      ),
      url: 'https://vvs.finance/farms',
    },
    {
      logo: '/images/bored-candy-city-logo.webp',
      title: 'Bored Candy City',
      subtitle: t('Provide {coin} Liquidity', {
        coin: 'VNO',
      }),
      description: t('Become a liquidity provider and earn trading fees'),
      tokens: (
        <>
          <TokenIcon token="vno" w="32px" h="32px" />
          <TokenIcon token="lcro" w="32px" h="32px" />
        </>
      ),
      url: 'https://candycity.finance/?chainId=25',
    },
    {
      logo: '/images/crypto-com-exchange-logo.webp',
      title: 'Crypto.com Exchange',
      subtitle: t('Trade {coin}', {
        coin: 'VNO',
      }),
      description: t(
        "Trade VNO on the world's fastest and most secure crypto exchange",
      ),
      tokens: <TokenIcon token="vno" w="32px" h="32px" />,
      url: 'https://crypto.com/exchange',
    },
  ];

  for (const item of list) {
    if (item) {
      result.push(item);
    }
  }

  return result;
};

const useSingleFinanceLendInfo = (
  options: UseQueryOptions<{
    tvlInUSD: number;
    apy: number;
  }> = {},
) => {
  return useQuery<{
    tvlInUSD: number;
    apy: number;
  }>(
    ['useSingleFinanceLendInfo'],
    async () => {
      const fetchResponse = await fetch(
        'https://api.singlefinance.io/api/info/vaults?chainid=25',
      );
      const response = (await fetchResponse.json()) as {
        data: {
          vaults: {
            name: string;
            tvlInUSD?: number;
            apy?: number;
          }[];
        };
      };

      const vnoTokenInfo = response.data.vaults.find((v) => v.name === 'ibVNO');

      return {
        tvlInUSD: vnoTokenInfo?.tvlInUSD ?? 0,
        apy: vnoTokenInfo?.apy ?? 0,
      };
    },
    {
      ...options,
    },
  );
};

const useSingleFinanceFarmInfo = (
  options: UseQueryOptions<{
    tvlInUSD: number;
    apy: number;
  }> = {},
) => {
  return useQuery<{
    tvlInUSD: number;
    apy: number;
  }>(
    ['useSingleFinanceFarmInfo'],
    async () => {
      const fetchResponse = await fetch(
        'https://api.singlefinance.io/api/info/farms?chainid=25&dex=vvs',
      );
      const response = (await fetchResponse.json()) as {
        data: {
          farms: {
            id: string;
            tvlInUSD?: number;
            bestOption?: {
              apy?: number;
            };
          }[];
        };
      };

      const vnoFarmInfo = response.data.farms.find(
        (v) => v.id === 'CRONOS-VVS-VVS-VNO',
      );

      return {
        tvlInUSD: vnoFarmInfo?.tvlInUSD ?? 0,
        apy: vnoFarmInfo?.bestOption?.apy ?? 0,
      };
    },
    {
      ...options,
    },
  );
};
